if (!customElements.get('aropuff-refill-picker')) {
  customElements.define(
    'aropuff-refill-picker',
    class AroPuffRefillPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.cards = Array.from(this.querySelectorAll('[data-refill-card]'));
        this.addButton = this.querySelector('[data-refill-add]');
        this.addLabel = this.querySelector('[data-refill-add-label]');
        this.totalQuantityElement = this.querySelector('[data-refill-total-quantity]');
        this.totalLabelElement = this.querySelector('[data-refill-total-label]');
        this.totalPriceElement = this.querySelector('[data-refill-total-price]');
        this.discountNote = this.querySelector('[data-refill-discount-note]');
        this.errorElement = this.querySelector('[data-refill-error]');
        this.statusElement = this.querySelector('[data-refill-status]');
        this.cartLink = this.querySelector('[data-refill-cart-link]');
        this.currencyCode = this.dataset.currencyCode || 'PLN';
        this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
        this.cartUrl = this.dataset.cartUrl || '/cart';
        this.isAdding = false;
        this.addedSelection = false;

        this.addEventListener('click', this.handleClick.bind(this));
        this.updateTotals();
      }

      handleClick(event) {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;

        if (button.matches('[data-refill-increment], [data-refill-decrement]')) {
          const card = button.closest('[data-refill-card]');
          if (!card) return;

          const currentQuantity = this.getCardQuantity(card);
          const maxQuantity = Number.parseInt(card.dataset.maxQuantity, 10) || 99;
          const nextQuantity = button.hasAttribute('data-refill-increment')
            ? Math.min(currentQuantity + 1, maxQuantity)
            : Math.max(currentQuantity - 1, 0);

          this.setCardQuantity(card, nextQuantity);
          this.resetAddedState();
          this.updateTotals();
          return;
        }

        if (button.matches('[data-refill-add]')) {
          this.addSelectionToCart();
        }
      }

      getCardQuantity(card) {
        const output = card.querySelector('[data-refill-quantity]');
        return Number.parseInt(output?.textContent || '0', 10) || 0;
      }

      setCardQuantity(card, quantity) {
        const output = card.querySelector('[data-refill-quantity]');
        const decrementButton = card.querySelector('[data-refill-decrement]');
        const incrementButton = card.querySelector('[data-refill-increment]');
        const maxQuantity = Number.parseInt(card.dataset.maxQuantity, 10) || 99;

        if (output) output.textContent = quantity;
        if (decrementButton) decrementButton.disabled = quantity === 0;
        if (incrementButton) incrementButton.disabled = quantity >= maxQuantity;
        card.classList.toggle('is-selected', quantity > 0);
      }

      getSelection() {
        return this.cards
          .map((card) => ({
            card,
            id: Number.parseInt(card.dataset.variantId, 10),
            price: Number.parseInt(card.dataset.unitPrice, 10) || 0,
            quantity: this.getCardQuantity(card),
          }))
          .filter((item) => item.id && item.quantity > 0);
      }

      updateTotals() {
        const selection = this.getSelection();
        const totalQuantity = selection.reduce((sum, item) => sum + item.quantity, 0);
        const totalPrice = selection.reduce((sum, item) => sum + item.price * item.quantity, 0);

        this.totalQuantityElement.textContent = totalQuantity;
        this.totalLabelElement.textContent = this.sachetNoun(totalQuantity);
        this.totalPriceElement.textContent = this.formatMoney(totalPrice);
        this.discountNote.classList.toggle('is-active', totalQuantity >= 4);

        if (!this.isAdding && !this.addedSelection) {
          this.addButton.disabled = totalQuantity === 0;
          this.addLabel.textContent = totalQuantity
            ? `Dodaj ${this.sachetAccusative(totalQuantity)} do koszyka`
            : 'Wybierz co najmniej 1 saszetkę';
        }
      }

      resetAddedState() {
        if (!this.addedSelection) return;
        this.addedSelection = false;
        this.statusElement.hidden = true;
        this.cartLink.hidden = true;
      }

      async addSelectionToCart() {
        if (this.isAdding) return;

        const selection = this.getSelection();
        const totalQuantity = selection.reduce((sum, item) => sum + item.quantity, 0);
        if (!totalQuantity) return;

        this.isAdding = true;
        this.errorElement.hidden = true;
        this.statusElement.hidden = true;
        this.cartLink.hidden = true;
        this.addButton.disabled = true;
        this.addButton.classList.add('is-loading');
        this.addLabel.textContent = 'Dodawanie do koszyka...';

        try {
          const response = await fetch(this.cartAddUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: selection.map((item) => ({ id: item.id, quantity: item.quantity })),
              sections: ['cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });

          const responseData = await response.json();
          if (!response.ok || responseData.status) {
            throw new Error(responseData.description || responseData.message || 'Nie udało się dodać produktów.');
          }

          await this.updateCartBubble(responseData.sections);
          this.addedSelection = true;
          this.statusElement.textContent = `Dodano ${this.sachetAccusative(totalQuantity)} do koszyka.`;
          this.statusElement.hidden = false;
          this.cartLink.hidden = false;
          this.addLabel.textContent = 'Dodano do koszyka ✓';
        } catch (error) {
          this.errorElement.textContent = error.message || 'Coś poszło nie tak. Spróbuj ponownie.';
          this.errorElement.hidden = false;
          this.addedSelection = false;
        } finally {
          this.isAdding = false;
          this.addButton.classList.remove('is-loading');

          if (!this.addedSelection) {
            this.updateTotals();
          }
        }
      }

      async updateCartBubble(renderedSections) {
        let sectionHtml = renderedSections?.['cart-icon-bubble'];

        if (!sectionHtml) {
          const separator = this.cartUrl.includes('?') ? '&' : '?';
          const response = await fetch(`${this.cartUrl}${separator}sections=cart-icon-bubble`);
          const data = await response.json();
          sectionHtml = data['cart-icon-bubble'];
        }

        if (!sectionHtml) return;

        const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const updatedBubble = parsedDocument.querySelector('#shopify-section-cart-icon-bubble, .shopify-section');
        const currentBubble = document.getElementById('cart-icon-bubble');

        if (updatedBubble && currentBubble) {
          currentBubble.innerHTML = updatedBubble.innerHTML;
        }
      }

      formatMoney(amount) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'pl-PL', {
            style: 'currency',
            currency: this.currencyCode,
          }).format(amount / 100);
        } catch (_error) {
          return `${(amount / 100).toFixed(2).replace('.', ',')} ${this.currencyCode}`;
        }
      }

      sachetNoun(quantity) {
        if (quantity === 1) return 'saszetka';
        const lastTwoDigits = quantity % 100;
        const lastDigit = quantity % 10;
        if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) return 'saszetki';
        return 'saszetek';
      }

      sachetAccusative(quantity) {
        return `${quantity} ${quantity === 1 ? 'saszetkę' : this.sachetNoun(quantity)}`;
      }
    }
  );
}
